import { Box, Button, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { BrandWordmark } from "../assets/BrandWordmark";
import { riseIn } from "../onboardingMotion";
import {
  eyebrowSx,
  primaryButtonSx,
  stepSubtitleSx,
  stepTitleSx,
  textSecondary,
} from "./stepStyles";

/**
 * Shared layout for the single-tap "moment" screens (mission, honesty,
 * showcase, aiShowcase): eyebrow and title anchored at the top, the body (and
 * optional visual) centered in the remaining space, CTA pinned to the bottom.
 * These are beats, not steps — the progress bar holds while they play.
 */

interface MomentStepProps {
  eyebrow: string;
  title: string;
  body: string;
  visual?: React.ReactNode;
  ctaLabel: string;
  onContinue: () => void;
  /** Center-align the text (mission/honesty read as statements). */
  centered?: boolean;
  /** Wordmark above the eyebrow — the anchor on screens with no visual of
      their own. Both screens that use it place it identically. */
  brandTop?: boolean;
  /** Optional no-penalty secondary under the CTA (the rating ask's
      "Maybe later"). */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export const MomentStep = ({
  eyebrow,
  title,
  body,
  visual,
  ctaLabel,
  onContinue,
  centered = false,
  brandTop = false,
  secondaryLabel,
  onSecondary,
}: MomentStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();

  const rise = (delay: number) => ({
    variants: riseIn(reduced ? 0 : delay),
    initial: "hidden" as const,
    animate: "visible" as const,
  });

  const bodyParagraphs = (
    <motion.div {...rise(0.16)}>
      {/* Blank lines in the copy become real paragraph breaks. */}
      {body.split("\n\n").map((paragraph, index) => (
        <Typography
          key={paragraph}
          sx={{
            ...stepSubtitleSx(isDark),
            mt: index === 0 ? 0 : 2,
            lineHeight: 1.6,
            maxWidth: centered ? 330 : undefined,
            mx: centered ? "auto" : undefined,
          }}
        >
          {paragraph}
        </Typography>
      ))}
    </motion.div>
  );

  return (
    <Box
      sx={{
        pt: 1,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        textAlign: centered ? "center" : "left",
      }}
    >
      {brandTop && (
        <motion.div {...rise(0)}>
          <BrandWordmark />
        </motion.div>
      )}
      <motion.div {...rise(0)}>
        <Typography sx={eyebrowSx(isDark)}>{eyebrow}</Typography>
      </motion.div>
      <motion.div {...rise(0.08)}>
        <Typography sx={{ ...stepTitleSx(isDark), mt: 0.75 }}>{title}</Typography>
      </motion.div>

      {/* Centered screens: visual then body float centered between the
          title and the CTA. */}
      {centered && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            py: 2,
          }}
        >
          {visual && (
            <motion.div {...rise(0.24)}>
              <Box sx={{ mb: 3.5, display: "flex", justifyContent: "center" }}>{visual}</Box>
            </motion.div>
          )}
          {bodyParagraphs}
        </Box>
      )}

      {/* Top-aligned screens: body follows the title; the visual centers
          itself in the remaining space. */}
      {!centered && (
        <>
          <Box sx={{ mt: 1.5 }}>{bodyParagraphs}</Box>
          {visual && (
            <motion.div {...rise(0.24)} style={{ flex: 1, display: "flex", minWidth: 0 }}>
              <Box
                sx={{
                  py: 2,
                  flex: 1,
                  // Load-bearing. The motion.div above is display:flex, so
                  // this Box is a flex item on a HORIZONTAL main axis, where
                  // `min-width: auto` resolves to min-content — a visual with
                  // any nowrap content (the AI demo's composer while it types)
                  // inflates this Box and pushes the whole visual rightwards.
                  // AiIntroStep never hit this because its wrapper sits in a
                  // COLUMN, where `min-width: auto` is 0 and the width just
                  // stretches to the container.
                  minWidth: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {visual}
              </Box>
            </motion.div>
          )}
          {!visual && <Box sx={{ flex: 1 }} />}
        </>
      )}

      <Button fullWidth onClick={onContinue} sx={{ ...primaryButtonSx, mt: 3 }}>
        {ctaLabel}
      </Button>
      {secondaryLabel && onSecondary && (
        <Button
          fullWidth
          variant="text"
          onClick={onSecondary}
          sx={{
            mt: 1,
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none",
            borderRadius: 2,
            color: textSecondary(isDark),
          }}
        >
          {secondaryLabel}
        </Button>
      )}
    </Box>
  );
};
